import { firebaseApp } from '@/firebase/init';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  QueryConstraint,
  setDoc,
  deleteDoc,
  where,
  WhereFilterOp,
} from 'firebase/firestore';

export const firestoreRef = getFirestore(firebaseApp);

const firestore: {
  create: (collectionName: string, data: { [key: string]: any }) => any;
  getOne: (path: string) => any;
  get: (
    path: string,
    options: {
      where?: [string, WhereFilterOp, unknown][];
      orderBy?: { [key: string]: 'asc' | 'desc' };
      limit?: number;
    }
  ) => any;
  update: (path: string, data: any) => any;
  delete: (path: string) => Promise<void>;
  sanitizeFields: (record: any) => any;
} = {
  create: async (collectionName, data) => {
    const docRef = await addDoc(collection(firestoreRef, collectionName), data);

    return {
      ...data,
      id: docRef.id,
    } as const;
  },

  getOne: async (path) => {
    const snapshot = await getDoc(doc(firestoreRef, path));
    return {
      id: snapshot.id,
      ...snapshot.data(),
    } as const;
  },

  get: async (path, options = {}) => {
    const constraints: QueryConstraint[] = [];

    if (options.where) {
      options.where.forEach((whereOptions) => {
        constraints.push(where(...whereOptions));
      });
    }

    if (options.orderBy) {
      Object.entries(options.orderBy).forEach(([key, order]) => {
        constraints.push(orderBy(key, order));
      });
    }

    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    const q = query(collection(firestoreRef, path), ...constraints);
    const snapshots = await getDocs(q);
    const docs = await Promise.all(snapshots.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    return docs;
  },

  update: async (path, data) => {
    const updated = await setDoc(doc(firestoreRef, path), data, { merge: true });
    return updated;
  },

  delete: async (path) => {
    await deleteDoc(doc(firestoreRef, path));
  },

  sanitizeFields: (record) => {
    return {
      ...record,
      created_at: record.created_at?.toDate ? +record.created_at.toDate() : record.created_at,
      updated_at: record.updated_at?.toDate ? +record.updated_at.toDate() : record.updated_at,
    };
  },
};

export default firestore;